/**
 * Audit KK4 — Cache-First-Wrapper.
 *
 * Wickelt einen beliebigen MarketDataProvider in eine Schicht, die zuerst die
 * Server-seitige Cache-Tabelle v5_market_data_cache und die History-Tabelle
 * v5_ohlcv_history liest. Beide werden ausschließlich SERVERSEITIG gefüllt
 * (`scripts/server-score-cron.ts`, Service-Role, läuft täglich in
 * `swingtrader-api.service` auf Hetzner) — nachweislich aktuell für 100 % der
 * aktiven Watchlist (Bars ≤ 1–3 Handelstage in jeder Rolle, Quotes taggleich
 * für alles außer Krypto, das separat über `v5_crypto_fundamentals` läuft).
 *
 * WARUM HIER KEIN WRITE-THROUGH MEHR STEHT (20.09.2026): Bis zur
 * RLS-Härtung im Mai (W73) schrieb diese Schicht Live-Treffer per
 * Write-Through zurück in den Cache. Seit W73 verlangen beide Tabellen die
 * Rolle `authenticated` für INSERT/UPDATE — geprüft, es gibt schlicht keine
 * Policy dafür, für keine Rolle. Jeder Schreibversuch aus dem Browser schlug
 * seitdem bei JEDEM Quote-/OHLCV-Aufruf fehl (abgefangen, geloggt, nie
 * sichtbar) — Ballast ohne Wirkung, weil der Cron die Tabellen ohnehin schon
 * aktuell hält. Der Browser LIEST — er schreibt nicht mehr in diese Tabellen.
 *
 * Fallback: wenn Cache leer oder zu alt → Live-Call wie bisher (nur zur
 * Anzeige, landet nicht mehr in der Datenbank).
 */

import { DataStateHelpers } from '@/types';
import type { DataState } from '@/types';

import { logCaughtError } from '../error-log';
import { supabase } from '../supabase';
import type {
  MarketDataProvider,
  OhlcvBar,
  OhlcvTimeframe,
  PriceQuote,
} from './types';

// Welle D — TTLs aus zentraler Definition
import {
  QUOTE_FRESH_MS,
  QUOTE_STALE_MAX_MS,
  OHLCV_FRESH_MS,
  OHLCV_STALE_MAX_MS,
  isFresh as isFreshTtl,
} from '../cache-ttls';

interface CacheRow {
  symbol: string;
  quote_data: PriceQuote | null;
  ohlcv_bars: OhlcvBar[] | null;
  quote_fetched_at: string | null;
  ohlcv_fetched_at: string | null;
}

function isFresh(fetchedAt: string | null, maxAgeMs: number): boolean {
  return isFreshTtl(fetchedAt, maxAgeMs);
}

async function readCacheRow(symbol: string): Promise<CacheRow | null> {
  const symU = symbol.toUpperCase();
  const { data, error } = await supabase
    .from('v5_market_data_cache')
    .select('symbol, quote_data, ohlcv_bars, quote_fetched_at, ohlcv_fetched_at')
    .eq('symbol', symU)
    .maybeSingle();
  if (error) {
    // Audit-Welle A: DB-Read-Fehler war stiller Schlucker
    logCaughtError('cached-provider.readCacheRow', error, {
      symbol: symU,
      severity: 'warn',
    });
    return null;
  }
  if (!data) return null;
  return data as unknown as CacheRow;
}

/**
 * W88 Mega-Audit Agent A — Crypto-Quote-Fallback aus v5_crypto_fundamentals.
 * refresh-coingecko (Cron alle 30min) schreibt aktuellen Crypto-Preis in
 * v5_crypto_fundamentals.current_price, NICHT in v5_market_data_cache.quote_data.
 * Damit Crypto-Symbole im UI nicht "NULL Quote" zeigen, lesen wir hier
 * direkt aus v5_crypto_fundamentals und mappen auf PriceQuote-Shape.
 */
async function readCryptoQuoteFromFundamentals(symbol: string): Promise<{ quote: PriceQuote; fetched_at: string } | null> {
  const symU = symbol.toUpperCase();
  const { data, error } = await supabase
    .from('v5_crypto_fundamentals')
    .select('symbol, current_price, market_cap_usd, total_volume_24h_usd, ath_usd, atl_usd, fetched_at')
    .eq('symbol', symU)
    .maybeSingle();
  if (error || !data || data.current_price == null) return null;
  // W88 Bugfix (2026-05-27) — Felder muessen exakt zu PriceQuote-Interface matchen.
  // Vorher: change_abs/change_pct/high_day/etc → falsche Feldnamen → UI crashed
  // mit "Cannot read 'toFixed' of undefined" beim Open von Crypto-Symbol-Detail.
  const currentPrice = Number(data.current_price);
  const ath = data.ath_usd != null ? Number(data.ath_usd) : currentPrice;
  const atl = data.atl_usd != null ? Number(data.atl_usd) : currentPrice;
  const marketCapBn = data.market_cap_usd != null ? Number(data.market_cap_usd) / 1e9 : null;
  const quote: PriceQuote = {
    symbol: symU,
    current_price: currentPrice,
    change_pct_today: 0, // Crypto-Fundamentals haben keinen Tageswert; UI zeigt 0%
    high_52w: ath, // Naehrungsweise: ATH als 52W-High (Crypto-Markt-Konvention)
    low_52w: atl, // ATL als 52W-Low
    market_cap_billions: marketCapBn,
    pe_ttm: null,
    fetched_at: data.fetched_at,
    return_5d_pct: null,
    return_13w_pct: null,
    return_26w_pct: null,
    return_52w_pct: null,
    roe_ttm_pct: null,
    drawdown_from_52w_high_pct: ath > 0 ? ((currentPrice - ath) / ath) * 100 : null,
    industry: 'Cryptocurrency',
  };
  return { quote, fetched_at: data.fetched_at };
}

/**
 * W88 P5 v2 (2026-05-27) — DataLoader-Style-Batching.
 *
 * Vorher: 60 Watchlist-Cells × 1 SELECT je Cell = 60 Supabase-Roundtrips
 *         → 5-6s LCP (Browser-HTTP-Connection-Limit ~6 parallel).
 *
 * Nachher: Alle innerhalb 10ms eingehenden readOhlcvHistory(daily,<=250)-Calls
 *          werden zu 1 RPC-Call (get_recent_ohlcv_batch via LATERAL JOIN)
 *          zusammengefasst. Transparent — kein React/UI-Code-Change.
 *
 * Aktivierung-Bedingung: timeframe === 'daily' UND limit <= 250.
 * Andere Anfragen (Chart MAX, weekly, monthly) fallen auf den klassischen
 * Single-Symbol-Pfad zurück.
 */
interface PendingRequest {
  symbol: string;
  limit: number;
  resolve: (data: OhlcvBar[] | null) => void;
  reject: (err: Error) => void;
}

let pendingDailyRequests: PendingRequest[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_WINDOW_MS = 10;
const BATCH_MAX_LIMIT = 250;
// W88-P6 (2026-05-27) — Postgres statement_timeout ist 8s. 62 Symbole × 250 Bars
// via LATERAL JOIN dauert >8s → 57014 "canceling statement".
// Iteration 1: chunk=20 → trotzdem Timeout (LATERAL skaliert schlecht).
// Iteration 2: chunk=10 → 6-7 parallele Calls für 62 Symbole, jeder <2s sicher.
const RPC_CHUNK_SIZE = 10;

interface BatchRpcRow {
  symbol: string;
  bar_date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

function flushBatch(): void {
  const batch = pendingDailyRequests;
  pendingDailyRequests = [];
  batchTimer = null;
  void executeBatch(batch);
}

async function executeBatch(batch: PendingRequest[]): Promise<void> {
  if (batch.length === 0) return;

  // Solo-Request: Kein Batching-Vorteil → direkter SELECT
  if (batch.length === 1) {
    const req = batch[0]!;
    try {
      const data = await readOhlcvHistoryDirect(req.symbol, 'daily', req.limit);
      req.resolve(data);
    } catch (err) {
      req.reject(err as Error);
    }
    return;
  }

  const uniqueSymbols = Array.from(new Set(batch.map((r) => r.symbol)));
  const maxBars = Math.max(...batch.map((r) => r.limit));

  // W88-P6: Chunk auf RPC_CHUNK_SIZE Symbole → mehrere parallele RPCs statt
  // ein riesiger LATERAL-JOIN über 60+ Symbole (löste statement_timeout aus).
  const chunks: string[][] = [];
  for (let i = 0; i < uniqueSymbols.length; i += RPC_CHUNK_SIZE) {
    chunks.push(uniqueSymbols.slice(i, i + RPC_CHUNK_SIZE));
  }

  try {
    const chunkResults = await Promise.all(
      chunks.map(async (chunk) => {
        const { data, error } = await supabase.rpc('get_recent_ohlcv_batch', {
          p_symbols: chunk,
          p_timeframe: 'daily',
          p_bars_per_symbol: maxBars,
        });
        return { chunk, data, error };
      })
    );

    // Group all results by symbol (jedes Chunk-Ergebnis liefert chronologisch ASC)
    const bySymbol = new Map<string, OhlcvBar[]>();
    const failedSymbols = new Set<string>();

    for (const { chunk, data, error } of chunkResults) {
      if (error || !data) {
        // Detaillierte Error-Info loggen (vorher kam nur 'Unknown error Object')
        const errInfo = {
          code: (error as { code?: string })?.code,
          message: (error as { message?: string })?.message,
          details: (error as { details?: string })?.details,
          hint: (error as { hint?: string })?.hint,
        };
        logCaughtError(
          'cached-provider.executeBatch.chunk',
          new Error(JSON.stringify(errInfo)),
          {
            severity: 'warn',
            context: { chunk_size: chunk.length, max_bars: maxBars, symbols: chunk.join(',') },
          }
        );
        for (const sym of chunk) failedSymbols.add(sym);
        continue;
      }

      for (const row of data as BatchRpcRow[]) {
        const symU = row.symbol.toUpperCase();
        const arr = bySymbol.get(symU) ?? [];
        arr.push({
          timestamp: row.bar_date,
          open: Number(row.open),
          high: Number(row.high),
          low: Number(row.low),
          close: Number(row.close),
          volume: Number(row.volume),
        });
        bySymbol.set(symU, arr);
      }
    }

    // Resolve: Cache-Hit aus Batch, sonst Fallback auf individuelle SELECTs
    for (const req of batch) {
      const bars = bySymbol.get(req.symbol);
      if (bars && bars.length > 0) {
        req.resolve(bars.slice(-req.limit));
      } else if (failedSymbols.has(req.symbol)) {
        // Chunk gescheitert → individueller SELECT als Fallback
        readOhlcvHistoryDirect(req.symbol, 'daily', req.limit)
          .then(req.resolve)
          .catch(req.reject);
      } else {
        // RPC hatte das Symbol nicht (= keine Bars in DB)
        req.resolve(null);
      }
    }
  } catch (err) {
    for (const req of batch) req.reject(err as Error);
  }
}

/**
 * Public batched read. Daily + limit<=250 wird zur Sammelanfrage; alles andere
 * geht direkt zum klassischen SELECT (siehe readOhlcvHistoryDirect).
 *
 * W88-P6 v4 (2026-05-27): DataLoader REAKTIVIERT nach Postgres-Function-Migration
 * (w88_p6_v4_get_recent_ohlcv_batch_rownumber). Function nutzt jetzt
 * ROW_NUMBER OVER PARTITION statt LATERAL JOIN + statement_timeout=30s.
 * Live-Test: 62 Symbole × 250 Bars = 15500 rows < 8s. RPC_CHUNK_SIZE bleibt
 * auf 10 (geringes Risiko, schnellere Wahrnehmung des ersten Chunks).
 */
const ENABLE_BATCH_LOADER = true;

async function readOhlcvHistory(
  symbol: string,
  timeframe: string,
  limit: number
): Promise<OhlcvBar[] | null> {
  if (ENABLE_BATCH_LOADER && timeframe === 'daily' && limit <= BATCH_MAX_LIMIT) {
    return new Promise<OhlcvBar[] | null>((resolve, reject) => {
      pendingDailyRequests.push({
        symbol: symbol.toUpperCase(),
        limit,
        resolve,
        reject,
      });
      if (batchTimer === null) {
        batchTimer = setTimeout(flushBatch, BATCH_WINDOW_MS);
      }
    });
  }
  return readOhlcvHistoryDirect(symbol, timeframe, limit);
}

/**
 * XX3 — Liest Bars aus persistenter History-Tabelle (Single-Symbol-SELECT).
 * Vor W88-P5 v2 hieß diese Funktion `readOhlcvHistory`; jetzt wird sie nur
 * noch von der Batching-Schicht oder für Long-History / weekly+monthly
 * direkt aufgerufen.
 */
async function readOhlcvHistoryDirect(
  symbol: string,
  timeframe: string,
  limit: number
): Promise<OhlcvBar[] | null> {
  const symU = symbol.toUpperCase();
  // W85 Fix-5 — PostgREST hat default max-rows=1000. Bei limit>1000 chunked
  // wir die Anfrage via .range(start, end) statt .limit(). Chunks à 1000 Bars
  // bis wir die gewuenschte Anzahl haben.
  const PG_MAX_ROWS = 1000;
  let allData: Array<{ bar_date: string; open: number; high: number; low: number; close: number; volume: number }> = [];

  if (limit <= PG_MAX_ROWS) {
    const { data, error } = await supabase
      .from('v5_ohlcv_history')
      .select('bar_date, open, high, low, close, volume')
      .eq('symbol', symU)
      .eq('timeframe', timeframe)
      .order('bar_date', { ascending: false })
      .limit(limit);
    if (error) {
      logCaughtError('cached-provider.readOhlcvHistoryDirect', error, {
        symbol: symU,
        severity: 'warn',
        context: { timeframe, limit },
      });
      return null;
    }
    allData = (data ?? []) as typeof allData;
  } else {
    // Chunked-Read fuer Long-History: Range-Pagination
    let offset = 0;
    while (offset < limit) {
      const chunkEnd = Math.min(offset + PG_MAX_ROWS - 1, limit - 1);
      const { data, error } = await supabase
        .from('v5_ohlcv_history')
        .select('bar_date, open, high, low, close, volume')
        .eq('symbol', symU)
        .eq('timeframe', timeframe)
        .order('bar_date', { ascending: false })
        .range(offset, chunkEnd);
      if (error) {
        logCaughtError('cached-provider.readOhlcvHistoryDirect', error, {
          symbol: symU,
          severity: 'warn',
          context: { timeframe, limit, offset, chunkEnd },
        });
        // Bei Fehler in chunk N+ trotzdem das was wir haben zurueckgeben
        if (allData.length === 0) return null;
        break;
      }
      const chunkData = (data ?? []) as typeof allData;
      if (chunkData.length === 0) break; // keine weiteren Bars
      allData = allData.concat(chunkData);
      if (chunkData.length < PG_MAX_ROWS) break; // letzter Chunk
      offset += PG_MAX_ROWS;
    }
  }

  if (allData.length === 0) return null;
  const data = allData;
  // chronologisch (älteste zuerst)
  const rows = (data as Array<{
    bar_date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }>).reverse();
  return rows.map((r) => ({
    timestamp: r.bar_date,
    open: r.open,
    high: r.high,
    low: r.low,
    close: r.close,
    volume: r.volume,
  }));
}

/**
 * BUGFIX-CHART (2026-05-14) — Liest Weekly-Bars aus v5_ohlcv_weekly View.
 * Die View aggregiert automatisch aus daily v5_ohlcv_history.
 */
async function readWeeklyHistory(
  symbol: string,
  limit: number
): Promise<OhlcvBar[] | null> {
  const { data, error } = await supabase
    .from('v5_ohlcv_weekly')
    .select('bar_date, open, high, low, close, volume')
    .eq('symbol', symbol.toUpperCase())
    .order('bar_date', { ascending: false })
    .limit(limit);
  if (error) {
    logCaughtError('cached-provider.readWeeklyHistory', error, {
      symbol,
      severity: 'warn',
      context: { limit },
    });
    return null;
  }
  if (!data || data.length === 0) return null;
  return (data as Array<{
    bar_date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }>)
    .map((r) => ({
      timestamp: r.bar_date,
      open: Number(r.open),
      high: Number(r.high),
      low: Number(r.low),
      close: Number(r.close),
      volume: Number(r.volume),
    }))
    .reverse(); // chronologisch
}

/**
 * BUGFIX-CHART (2026-05-14) — Aggregiert Monthly-Bars on-the-fly aus Daily.
 * Keine eigene v5_ohlcv_monthly View vorhanden — wir gruppieren YYYY-MM.
 * Bei 'limit=60 monatliche Bars' brauchen wir ~60 * 22 = 1320 daily Bars.
 */
async function readMonthlyHistoryAggregated(
  symbol: string,
  limitMonths: number
): Promise<OhlcvBar[] | null> {
  // 22 Trading-Days pro Monat × Sicherheit-Faktor
  // W85 — von 2500 auf 15000 erhöht (Stooq-DB liefert für AAPL 10.500+ Bars =
  // 41 Jahre; bei monthly-MAX = 600 Monate × 25 = 15000 Daily-Bars benötigt).
  const neededDailyBars = Math.min(15000, limitMonths * 25);
  const daily = await readOhlcvHistory(symbol, 'daily', neededDailyBars);
  if (!daily || daily.length === 0) return null;

  // Gruppieren nach YYYY-MM
  type Agg = { open: number; high: number; low: number; close: number; volume: number; firstDate: string };
  const byMonth = new Map<string, Agg>();
  for (const bar of daily) {
    const key = bar.timestamp.slice(0, 7); // YYYY-MM
    const existing = byMonth.get(key);
    if (!existing) {
      byMonth.set(key, {
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume,
        firstDate: bar.timestamp,
      });
    } else {
      existing.high = Math.max(existing.high, bar.high);
      existing.low = Math.min(existing.low, bar.low);
      existing.close = bar.close; // letzter Wert = month close
      existing.volume += bar.volume;
    }
  }

  // Sort by month ascending, dann die letzten N Monate
  const months = Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-limitMonths);

  return months.map(([, agg]) => ({
    timestamp: agg.firstDate, // first-day-of-month als Anchor
    open: agg.open,
    high: agg.high,
    low: agg.low,
    close: agg.close,
    volume: agg.volume,
  }));
}

/**
 * BUGFIX-CHART (2026-05-14) — Unified-Reader für alle Timeframes aus DB.
 */
async function readHistoryByTimeframe(
  symbol: string,
  timeframe: string,
  limit: number
): Promise<OhlcvBar[] | null> {
  // W88 (2026-05-27) — 'hourly' liest aus separater Tabelle v5_ohlcv_hourly
  // (bar_ts TIMESTAMPTZ, nicht bar_date DATE wie history-Tabelle).
  if (timeframe === 'hourly') return readHourlyHistory(symbol, limit);
  if (timeframe === 'daily') return readOhlcvHistory(symbol, 'daily', limit);
  if (timeframe === 'weekly') return readWeeklyHistory(symbol, limit);
  if (timeframe === 'monthly') return readMonthlyHistoryAggregated(symbol, limit);
  return null;
}

/**
 * W88 (2026-05-27) — Liest Hourly-Bars aus v5_ohlcv_hourly Tabelle.
 * bar_ts ist TIMESTAMPTZ (volle Stunden-Aufloesung), nicht DATE wie history.
 */
async function readHourlyHistory(
  symbol: string,
  limit: number
): Promise<OhlcvBar[] | null> {
  const symU = symbol.toUpperCase();
  const PG_MAX_ROWS = 1000;
  let allData: Array<{ bar_ts: string; open: number; high: number; low: number; close: number; volume: number }> = [];

  if (limit <= PG_MAX_ROWS) {
    const { data, error } = await supabase
      .from('v5_ohlcv_hourly')
      .select('bar_ts, open, high, low, close, volume')
      .eq('symbol', symU)
      .order('bar_ts', { ascending: false })
      .limit(limit);
    if (error) {
      logCaughtError('cached-provider.readHourlyHistory', error, {
        symbol: symU,
        severity: 'warn',
        context: { limit },
      });
      return null;
    }
    allData = (data ?? []) as typeof allData;
  } else {
    let offset = 0;
    while (offset < limit) {
      const chunkEnd = Math.min(offset + PG_MAX_ROWS - 1, limit - 1);
      const { data, error } = await supabase
        .from('v5_ohlcv_hourly')
        .select('bar_ts, open, high, low, close, volume')
        .eq('symbol', symU)
        .order('bar_ts', { ascending: false })
        .range(offset, chunkEnd);
      if (error) {
        if (allData.length === 0) return null;
        break;
      }
      const chunkData = (data ?? []) as typeof allData;
      if (chunkData.length === 0) break;
      allData = allData.concat(chunkData);
      if (chunkData.length < PG_MAX_ROWS) break;
      offset += PG_MAX_ROWS;
    }
  }

  if (allData.length === 0) return null;
  // chronologisch (älteste zuerst) für Chart-Display
  return allData.reverse().map((r) => ({
    timestamp: r.bar_ts,
    open: r.open,
    high: r.high,
    low: r.low,
    close: r.close,
    volume: r.volume,
  }));
}

function inferAssetClass(symbol: string): string {
  const u = symbol.toUpperCase();
  if (u.includes('/') || u.endsWith('USDT')) return 'crypto';
  return 'stock';
}

/**
 * Wickelt einen Provider mit Server-Cache. Lookup und Provider-Eigenschaften
 * werden direkt durchgereicht — Cache greift nur bei Quote und Daily-OHLCV.
 */
export function withSupabaseCache(inner: MarketDataProvider): MarketDataProvider {
  return {
    name: `${inner.name} + Cache`,
    is_live: inner.is_live,

    lookupSymbol(query, signal) {
      return inner.lookupSymbol(query, signal);
    },

    async getQuote(symbol, signal): Promise<DataState<PriceQuote>> {
      const row = await readCacheRow(symbol);
      if (row && row.quote_data !== null && isFresh(row.quote_fetched_at, QUOTE_FRESH_MS)) {
        const cachedQuote: PriceQuote = row.quote_data;
        return DataStateHelpers.success(cachedQuote, 60);
      }
      // W88 Mega-Audit Agent A — Crypto-Quote-Fallback: refresh-coingecko cron
      // schreibt aktuellen Crypto-Preis in v5_crypto_fundamentals (alle 30min),
      // NICHT in v5_market_data_cache. Crypto-Symbole hatten dadurch immer
      // NULL-Quote-Data → TwelveData-Fallback wurde unnoetig getriggert.
      if (inferAssetClass(symbol) === 'crypto') {
        const cryptoFallback = await readCryptoQuoteFromFundamentals(symbol);
        if (cryptoFallback && isFresh(cryptoFallback.fetched_at, QUOTE_STALE_MAX_MS)) {
          return DataStateHelpers.success(cryptoFallback.quote, 60);
        }
      }
      const live = await inner.getQuote(symbol, signal);
      if (live.status === 'success' && live.data !== null && live.data.current_price > 0) {
        return live;
      }

      // Welle 12 — Wochenenden / Provider-Ausfall:
      // Wenn Live-Provider keine sinnvolle Quote liefert (null / current_price=0),
      // greifen wir auf die letzte gecachte Quote zurück.
      // Welle D — ABER nur wenn nicht zu alt (max 48h). Sonst wäre die Quote
      // bei einer langen Provider-Ausfall-Phase wochenlang stehengeblieben
      // und der Score würde mit falschen Technicals rechnen.
      if (row && row.quote_data !== null) {
        const ageMs = row.quote_fetched_at
          ? Date.now() - new Date(row.quote_fetched_at).getTime()
          : Infinity;
        if (ageMs <= QUOTE_STALE_MAX_MS) {
          return DataStateHelpers.success(row.quote_data, 60);
        }
      }
      return live;
    },

    async getOhlcv(
      symbol,
      timeframe: OhlcvTimeframe,
      bars: number,
      signal
    ): Promise<DataState<OhlcvBar[]>> {
      // XX3 + BUGFIX-CHART (2026-05-14): Persistente History für ALLE
      // Timeframes lesen, nicht nur daily. Weekly/Monthly via View bzw.
      // Aggregation. Überlebt Cache-Reset UND Provider-Ausfall.
      const histBars = await readHistoryByTimeframe(symbol, timeframe, bars);
      if (histBars && histBars.length >= bars) {
        // Frische prüfen.
        // W85 — Daily-Frische von 8h auf 4 Tage erhöht (Wochenend-Toleranz):
        // Stooq-Daten kommen Freitag-Schluss; Montag/Dienstag sollen die DB-Bars
        // weiterhin als frisch gelten statt unnötig auf Live-API zu fallen.
        // Weekly: 7 Tage, Monthly: 35 Tage Toleranz unverändert.
        const newest = histBars[histBars.length - 1];
        const freshMs =
          timeframe === 'daily'
            ? 4 * 24 * 60 * 60 * 1000 // 4 Tage statt 8h
            : timeframe === 'weekly'
              ? 7 * 24 * 60 * 60 * 1000
              : 35 * 24 * 60 * 60 * 1000;
        if (newest && isFresh(newest.timestamp, freshMs)) {
          return DataStateHelpers.success(histBars.slice(-bars), 30 * 60);
        }
      }
      // W85 Fix-3 — DB-First nur fuer Long-History-Anfragen (z.B. Chart MAX
      // mit 15000 bars). Vermeidet dass Watchlist-Score-Cells (250 bars)
      // ploetzlich 10000+ Bars bekommen → Render-Overflow / React Error #310.
      // Nur wenn bars > 2000 (= explizit Long-History) → DB nutzen sobald sie
      // mehr Bars hat als der Live-Provider liefern könnte (~1000).
      // W85 Fix-4 — Bei Long-History KEINE Frische-Pruefung mehr: User schaut
      // historisch zurueck (41 Jahre), ob letzter Bar 1 oder 5 Tage alt ist
      // visualisiert keinen Unterschied. Tagesaktuelle Bars holt ohnehin der
      // Live-Provider fuer die 1Y/5Y-Charts.
      if (bars > 2000 && histBars && histBars.length >= 1000 && timeframe === 'daily') {
        return DataStateHelpers.success(histBars, 30 * 60);
      }

      // Fallback 1 (nur daily): Live-Snapshot-Cache mit ohlcv_bars-Array
      if (timeframe === 'daily') {
        const row = await readCacheRow(symbol);
        if (
          row &&
          row.ohlcv_bars !== null &&
          row.ohlcv_bars.length > 0 &&
          isFresh(row.ohlcv_fetched_at, OHLCV_FRESH_MS)
        ) {
          const cachedBars: OhlcvBar[] = row.ohlcv_bars;
          if (cachedBars.length >= bars) {
            return DataStateHelpers.success(cachedBars.slice(-bars), 30 * 60);
          }
        }
      }

      const live = await inner.getOhlcv(symbol, timeframe, bars, signal);
      if (live.status === 'success' && live.data !== null && live.data.length > 0 && timeframe === 'daily') {
        return live;
      }

      // BUGFIX-CHART (2026-05-14): Stale-Fallback für ALLE Timeframes.
      // Wenn Live-Provider versagt, nehmen wir DB-Bars auch wenn sie älter sind
      // (bis zur Stale-Grenze). Lieber 7 Tage alter Wochenchart als gerade Linie.
      if (live.status !== 'success' || !live.data || live.data.length === 0) {
        const stale = await readHistoryByTimeframe(symbol, timeframe, bars);
        if (stale && stale.length > 0) {
          const newest = stale[stale.length - 1];
          const ageMs = newest?.timestamp
            ? Date.now() - new Date(newest.timestamp).getTime()
            : Infinity;
          // Stale-Limit je Timeframe großzügig
          const staleLimit =
            timeframe === 'daily'
              ? OHLCV_STALE_MAX_MS
              : timeframe === 'weekly'
                ? 30 * 24 * 60 * 60 * 1000
                : 120 * 24 * 60 * 60 * 1000;
          if (ageMs <= staleLimit) {
            return DataStateHelpers.success(stale.slice(-bars), 30 * 60);
          }
        }
      }
      return live;
    },
  };
}
